import { useState, useEffect } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Scale, Loader2, Clock, ChevronRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Markdown } from "@/components/Markdown";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Consultation({ user, setUser }) {
  var [consultations, setConsultations] = useState([]);
  var [subject, setSubject] = useState("");
  var [description, setDescription] = useState("");
  var [urgency, setUrgency] = useState("normal");
  var [loading, setLoading] = useState(false);
  var [currentResult, setCurrentResult] = useState(null);
  var [showHistory, setShowHistory] = useState(false);

  useEffect(function() { fetchConsultations(); }, []);

  async function fetchConsultations() {
    try {
      var res = await fetch(API_URL + "/api/consultations", { credentials: "include" });
      if (res.ok) setConsultations(await res.json());
    } catch (e) { }
  }

  async function handleSubmit() {
    if (!subject.trim() || !description.trim()) { toast.error("Sujet et description requis"); return; }
    setLoading(true); setCurrentResult(null);
    try {
      var res = await fetch(API_URL + "/api/consultations", {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ subject: subject, description: description, urgency: urgency })
      });
      if (res.ok) {
        var data = await res.json();
        setCurrentResult(data);
        fetchConsultations();
        toast.success("Consultation terminée");
      } else { var err = await res.json(); toast.error(err.detail || "Erreur"); }
    } catch (e) { toast.error("Erreur de connexion"); }
    setLoading(false);
  }

  async function loadConsultation(id) {
    try {
      var res = await fetch(API_URL + "/api/consultations/" + id, { credentials: "include" });
      if (res.ok) { setCurrentResult(await res.json()); setShowHistory(false); }
    } catch (e) { toast.error("Erreur"); }
  }

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8" data-testid="consultation-page">
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-semibold tracking-tight mb-1">Consultation juridique</h1>
          <p className="text-sm text-muted-foreground">Obtenez un avis juridique instantané par IA (GPT-5.2)</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {!currentResult && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Nouvelle consultation</CardTitle>
                  <CardDescription>Décrivez votre situation juridique</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Sujet</Label>
                    <Input value={subject} onChange={function(e) { setSubject(e.target.value); }} placeholder="Ex: Rupture de contrat commercial" data-testid="consult-subject" />
                  </div>
                  <div>
                    <Label>Urgence</Label>
                    <Select value={urgency} onValueChange={setUrgency}>
                      <SelectTrigger data-testid="consult-urgency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="faible">Faible</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Description détaillée</Label>
                    <Textarea value={description} onChange={function(e) { setDescription(e.target.value); }} rows={6} placeholder="Décrivez en détail votre situation..." data-testid="consult-description" />
                  </div>
                  <Button onClick={handleSubmit} disabled={loading || !subject.trim() || !description.trim()} className="w-full" data-testid="consult-submit-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    {loading ? "Analyse en cours..." : "Obtenir un avis juridique"}
                  </Button>
                </CardContent>
              </Card>
            )}

            {currentResult && (
              <Card data-testid="consult-result">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{currentResult.subject}</CardTitle>
                    <Badge variant="secondary">GPT-5.2</Badge>
                  </div>
                  <CardDescription>{new Date(currentResult.created_at).toLocaleString("fr-FR")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm max-w-none">
                    <div className="text-sm leading-relaxed text-slate-700"><Markdown content={currentResult.response} /></div>
                  </div>
                  <Button variant="outline" className="mt-4" onClick={function() { setCurrentResult(null); setSubject(""); setDescription(""); }} data-testid="new-consult-btn">
                    Nouvelle consultation
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2"><Scale className="w-5 h-5 text-blue-600" />Historique</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {consultations.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucune consultation</p>
                ) : consultations.map(function(c) {
                  return (
                    <div key={c.consultation_id} className="p-2 rounded-md hover:bg-slate-50 cursor-pointer transition-colors" onClick={function() { loadConsultation(c.consultation_id); }} data-testid={"history-" + c.consultation_id}>
                      <p className="text-sm font-medium truncate">{c.subject}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(c.created_at).toLocaleDateString("fr-FR")}</span>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
